import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { AFTER_PROCESSING, MAILBOX_PURPOSES, checkMailbox, createMailbox, deleteMailbox, listMailboxes, pollMailbox, updateMailbox, type Mailbox as MailboxSchema } from '@mkcore/platform/schema';
import type { z } from '@mkcore/shared';
import { Badge, Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, Mono, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { ApiError, call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { ConfirmButton } from '@/components/confirm';

type Mailbox = z.output<typeof MailboxSchema>;

export const Route = createFileRoute('/_app/admin/mailboxes')({ component: Mailboxes });

export const mailboxesQuery = queryOptions({ queryKey: ['admin', 'mailbox', 'list'], queryFn: () => call(listMailboxes, {}), refetchInterval: 30_000 });

const statusTone = { ok: 'success', error: 'danger', off: 'outline' } as const;

/** The mailboxes the app reads (CO-0764, CO-0974): state, subscription, filters, and check or fetch now. */
function Mailboxes() {
  const { t } = useTranslation();
  const q = useQuery(mailboxesQuery);
  const canUpdate = useCan('admin:update');
  const canDelete = useCan('admin:delete');
  const [editing, setEditing] = useState<Mailbox | 'new' | null>(null);
  const remove = useActionMutation(deleteMailbox, { invalidate: [['admin', 'mailbox']], success: t('mailboxes.deleted') });
  const check = useActionMutation(checkMailbox, { invalidate: [['admin', 'mailbox']] });
  const poll = useActionMutation(pollMailbox, { invalidate: [['admin', 'mailbox'], ['admin', 'inboundMail']], success: (r) => t('mailboxes.fetched', { count: r.fetched }) });
  return (
    <>
      <PageHeader
        eyebrow={t('nav.admin')}
        title={t('mailboxes.title')}
        description={t('mailboxes.description')}
        actions={
          canUpdate && (
            <Button onClick={() => setEditing('new')}>
              <Plus /> {t('mailboxes.add')}
            </Button>
          )
        }
      />
      {!q.data ? (
        <Skeleton className="h-48" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('mailboxes.empty')} description={t('mailboxes.emptyHint')} />
      ) : (
        <div className="grid gap-4">
          {q.data.items.map((m) => (
            <Card key={m.id} role="region" className="grid gap-3 p-4" aria-label={m.address}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold">{m.address}</h2>
                  <Badge tone={statusTone[m.status]}>{t(`mailboxes.status.${m.status}`)}</Badge>
                  <Badge tone="outline">{t(`mailboxes.purpose.${m.purpose}`)}</Badge>
                  {m.purpose === 'log' && m.triageUnknown && <Badge tone="outline">{t('mailboxes.triageBadge')}</Badge>}
                </div>
                {canUpdate && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" loading={check.isPending && check.variables?.input.id === m.id} onClick={() => check.mutate({ input: { id: m.id } })}>
                      <ShieldCheck /> {t('mailboxes.check')}
                    </Button>
                    <Button size="sm" variant="secondary" loading={poll.isPending && poll.variables?.input.id === m.id} onClick={() => poll.mutate({ input: { id: m.id } })}>
                      <RefreshCw /> {t('mailboxes.poll')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditing(m)}>
                      <Pencil /> {t('common.edit')}
                    </Button>
                    {canDelete && (
                      <ConfirmButton
                        icon={<Trash2 />}
                        label={t('common.delete')}
                        variant="danger"
                        title={t('mailboxes.deleteTitle', { address: m.address })}
                        description={t('mailboxes.deleteText')}
                        confirmLabel={t('common.delete')}
                        onConfirm={() => remove.mutateAsync({ input: { id: m.id }, ifMatch: m.version })}
                      />
                    )}
                  </div>
                )}
              </div>
              {m.lastError && (
                <p role="alert" className="text-sm text-danger">
                  {m.lastError}
                </p>
              )}
              <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <Row label={t('mailboxes.fields.folder')}>{m.folder}</Row>
                <Row label={t('mailboxes.fields.afterProcessing')}>{m.afterProcessing === 'move' ? t('mailboxes.moveTo', { folder: m.moveToFolder }) : t('mailboxes.categoryMark')}</Row>
                <Row label={t('mailboxes.subscription')}>{m.subscriptionExpiresAt ? t('mailboxes.until', { at: formatDateTime(m.subscriptionExpiresAt) }) : '–'}</Row>
                <Row label={t('mailboxes.lastMessage')}>{m.lastMessageAt ? formatDateTime(m.lastMessageAt) : '–'}</Row>
                <Row label={t('mailboxes.fields.senderAllow')}>{m.senderAllow.join(', ') || t('mailboxes.everyone')}</Row>
                <Row label={t('mailboxes.fields.senderBlock')}>{m.senderBlock.join(', ') || '–'}</Row>
                <Row label={t('mailboxes.notificationUrl')}>
                  <Mono className="break-all text-xs">{m.notificationUrl}</Mono>
                </Row>
              </dl>
              <div className="flex flex-wrap gap-2 text-sm">
                {Object.entries(m.counts)
                  .filter(([, n]) => n > 0)
                  .map(([status, n]) => (
                    <Link key={status} to="/admin/inbound-mails" search={{ mailboxId: m.id, status: status as never }} className="underline">
                      {t(`inboundMails.status.${status}`)}: {n}
                    </Link>
                  ))}
              </div>
            </Card>
          ))}
        </div>
      )}
      {editing && <MailboxDialog mailbox={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[11rem_1fr] gap-2">
      <dt className="text-fg-3">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

const list = (s: string) =>
  s
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

function MailboxDialog({ mailbox, onClose }: { mailbox?: Mailbox; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [f, setF] = useState({
    address: mailbox?.address ?? '',
    purpose: mailbox?.purpose ?? 'intake',
    folder: mailbox?.folder ?? 'inbox',
    senderAllow: mailbox?.senderAllow.join(', ') ?? '',
    senderBlock: mailbox?.senderBlock.join(', ') ?? '',
    afterProcessing: mailbox?.afterProcessing ?? 'category',
    moveToFolder: mailbox?.moveToFolder ?? '',
    enabled: mailbox?.enabled ?? true,
    triageUnknown: mailbox?.triageUnknown ?? false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const onError = (e: unknown) => setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: message(e) } : { _: message(e) });
  const create = useActionMutation(createMailbox, { invalidate: [['admin', 'mailbox']], silentErrors: true, onSuccess: onClose });
  const update = useActionMutation(updateMailbox, { invalidate: [['admin', 'mailbox']], silentErrors: true, onSuccess: onClose });
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));
  const submit = () => {
    const fields = {
      folder: f.folder.trim() || 'inbox',
      senderAllow: list(f.senderAllow),
      senderBlock: list(f.senderBlock),
      afterProcessing: f.afterProcessing,
      moveToFolder: f.afterProcessing === 'move' ? f.moveToFolder.trim() || null : null,
      enabled: f.enabled,
      purpose: f.purpose,
      triageUnknown: f.purpose === 'log' && f.triageUnknown,
    };
    if (mailbox) update.mutate({ input: { id: mailbox.id, ...fields }, ifMatch: mailbox.version }, { onError });
    else create.mutate({ input: { address: f.address.trim(), ...fields } }, { onError });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mailbox ? t('mailboxes.edit') : t('mailboxes.add')}</DialogTitle>
        </DialogHeader>
        <form
          id="mailbox-form"
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
            {/* The address is the mailbox: to read another one, add it. The purpose can change (log, intake). */}
            <Field label={t('mailboxes.fields.address')} error={errors.address}>
              {(p) => <Input {...p} type="email" value={f.address} disabled={!!mailbox} onChange={(e) => set({ address: e.target.value })} />}
            </Field>
            <Field label={t('mailboxes.fields.purpose')}>
              {(p) => (
                <NativeSelect {...p} value={f.purpose} onChange={(e) => set({ purpose: e.target.value as never })}>
                  {MAILBOX_PURPOSES.map((x) => (
                    <option key={x} value={x}>
                      {t(`mailboxes.purpose.${x}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          <Field label={t('mailboxes.fields.folder')} hint={t('mailboxes.folderHint')} error={errors.folder}>
            {(p) => <Input {...p} value={f.folder} onChange={(e) => set({ folder: e.target.value })} />}
          </Field>
          <Field label={t('mailboxes.fields.senderAllow')} hint={t('mailboxes.senderHint')} error={errors.senderAllow}>
            {(p) => <Input {...p} value={f.senderAllow} onChange={(e) => set({ senderAllow: e.target.value })} />}
          </Field>
          <Field label={t('mailboxes.fields.senderBlock')} error={errors.senderBlock}>
            {(p) => <Input {...p} value={f.senderBlock} onChange={(e) => set({ senderBlock: e.target.value })} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('mailboxes.fields.afterProcessing')}>
              {(p) => (
                <NativeSelect {...p} value={f.afterProcessing} onChange={(e) => set({ afterProcessing: e.target.value as never })}>
                  {AFTER_PROCESSING.map((x) => (
                    <option key={x} value={x}>
                      {t(`mailboxes.after.${x}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {f.afterProcessing === 'move' && (
              <Field label={t('mailboxes.fields.moveToFolder')} error={errors.moveToFolder}>
                {(p) => <Input {...p} value={f.moveToFolder} onChange={(e) => set({ moveToFolder: e.target.value })} />}
              </Field>
            )}
          </div>
          {f.purpose === 'log' && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.triageUnknown} onChange={(e) => set({ triageUnknown: e.target.checked })} /> {t('mailboxes.fields.triageUnknown')}
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> {t('mailboxes.fields.enabled')}
          </label>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="mailbox-form" disabled={!mailbox && !f.address.trim()} loading={create.isPending || update.isPending}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
